/**
 * useAutoSave Hook
 * 
 * Automatically saves content when there are unsaved changes.
 * Features:
 * - Activity-aware: waits for user to stop typing before saving
 * - Debounced: saves X seconds after last user activity
 * - Max interval: saves at least every Y minutes (from user settings)
 * 
 * Usage:
 * ```tsx
 * useAutoSave({
 *   hasUnsavedChanges: unsavedChanges.hasChanges,
 *   saveHandler: handleSave,
 *   enabled: wizardMode === 'edit',
 *   activityTimestamp: lastEditTimestamp, // Update this on every user input
 * });
 * ```
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

interface UseAutoSaveOptions {
  /** Whether there are currently unsaved changes */
  hasUnsavedChanges: boolean;
  /** The function to call to save changes */
  saveHandler?: () => Promise<void> | void;
  /** Whether auto-save is enabled (default: true) */
  enabled?: boolean;
  /** Override the auto-save interval in minutes (optional, otherwise uses user settings) */
  intervalOverride?: number;
  /** Timestamp of last user activity - updates on every input change to delay save while typing */
  activityTimestamp?: number;
  /** Time in seconds to wait after last activity before saving (default: 3) */
  idleDelaySeconds?: number;
}

interface UseAutoSaveReturn {
  /** The current auto-save interval in minutes (0 = disabled) */
  autoSaveInterval: number;
  /** Whether auto-save is currently active (has pending timer) */
  isAutoSaveActive: boolean;
}

export function useAutoSave({
  hasUnsavedChanges,
  saveHandler,
  enabled = true,
  intervalOverride,
  activityTimestamp,
  idleDelaySeconds = 3,
}: UseAutoSaveOptions): UseAutoSaveReturn {
  const { currentUser } = useSimplifiedUserData();
  
  // Get auto-save interval from user settings (in minutes), default to 5
  const autoSaveInterval = intervalOverride ?? currentUser?.settings?.autoSaveInterval ?? 5;
  
  // Track the max interval timer (fallback save)
  const maxIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Track the idle delay timer (activity-based save)
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isAutoSaveActiveRef = useRef(false);
  
  // Track if a save is currently in progress to prevent overlapping saves
  const isSavingRef = useRef(false);
  
  // Track the last time we performed an auto-save
  const lastAutoSaveTimeRef = useRef<number>(0);
  
  // Use ref for saveHandler to avoid recreating the interval on every render
  const saveHandlerRef = useRef(saveHandler);
  useEffect(() => {
    saveHandlerRef.current = saveHandler;
  }, [saveHandler]);

  // Perform the auto-save
  const performAutoSave = useCallback(async () => {
    if (isSavingRef.current) {
      return;
    }
    
    if (!saveHandlerRef.current) {
      return;
    }

    try {
      isSavingRef.current = true;
      // #region agent log
      if(import.meta.env.DEV){fetch('http://127.0.0.1:7242/ingest/8fd71a2b-62c7-4902-9a51-278ccbdfc26a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useAutoSave.ts:performAutoSave',message:'AUTO-SAVE FIRING',data:{timeSinceLastSave:Date.now()-lastAutoSaveTimeRef.current},timestamp:Date.now(),hypothesisId:'H-D'})}).catch(()=>{});}
      // #endregion
      await saveHandlerRef.current();
      lastAutoSaveTimeRef.current = Date.now();
    } catch (error) {
      // Don't throw - we don't want to break the timers
    } finally {
      isSavingRef.current = false;
    }
  }, []);

  // Activity-based auto-save: wait for idle period after user stops typing
  useEffect(() => {
    // Clear any existing idle timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    // Don't set up idle timer if:
    // - Auto-save is disabled
    // - No unsaved changes
    // - No save handler
    // - No activity timestamp provided (means component doesn't track activity)
    if (!enabled || autoSaveInterval <= 0 || !hasUnsavedChanges || !saveHandler || !activityTimestamp) {
      return;
    }

    // Convert idle delay to milliseconds
    const idleDelayMs = idleDelaySeconds * 1000;

    // Set up idle timer - will trigger after user stops typing for X seconds
    idleTimerRef.current = setTimeout(() => {
      performAutoSave();
    }, idleDelayMs);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [enabled, autoSaveInterval, hasUnsavedChanges, saveHandler, activityTimestamp, idleDelaySeconds, performAutoSave]);

  // Max interval auto-save: ensure we save at least every X minutes (fallback)
  useEffect(() => {
    // Clear any existing max interval
    if (maxIntervalRef.current) {
      clearInterval(maxIntervalRef.current);
      maxIntervalRef.current = null;
      isAutoSaveActiveRef.current = false;
    }

    // Don't set up max interval if:
    // - Auto-save is disabled
    // - Auto-save interval is 0 (disabled in settings)
    // - No unsaved changes
    // - No save handler
    if (!enabled || autoSaveInterval <= 0 || !hasUnsavedChanges || !saveHandler) {
      return;
    }

    // Convert minutes to milliseconds
    const intervalMs = autoSaveInterval * 60 * 1000;

    // Set up the max interval as a fallback
    // This ensures we save at least every X minutes, even if user is continuously typing
    maxIntervalRef.current = setInterval(() => {
      // Only save if enough time has passed since last auto-save
      // This prevents double-saving if the idle timer just fired
      const timeSinceLastSave = Date.now() - lastAutoSaveTimeRef.current;
      const minTimeBetweenSaves = idleDelaySeconds * 1000;
      
      if (timeSinceLastSave >= minTimeBetweenSaves) {
        performAutoSave();
      }
    }, intervalMs);
    
    isAutoSaveActiveRef.current = true;

    // Cleanup on unmount or when dependencies change
    return () => {
      if (maxIntervalRef.current) {
        clearInterval(maxIntervalRef.current);
        maxIntervalRef.current = null;
        isAutoSaveActiveRef.current = false;
      }
    };
  }, [enabled, autoSaveInterval, hasUnsavedChanges, saveHandler, idleDelaySeconds, performAutoSave]);

  return {
    autoSaveInterval,
    isAutoSaveActive: isAutoSaveActiveRef.current,
  };
}

export default useAutoSave;
